/**
 * INCOME ONLY NEEDS THE CLIENT'S CONFIRMATION, IN EVERY MODULE, TESTED OR HAND-BUILT (owner, 21 Oct 2026; the
 * guaranteed income review E5/M2/R1 and the agreed mock-up, docs/ux-review/annuities/).
 *
 * A lifetime annuity with nothing returned pays the most, and in return the money is gone for good: nothing
 * to the family on death, even soon after buying it, and no cash-out value if plans change. A plan with one
 * cannot be chosen or saved until the client has confirmed it — and the confirmation is a RECORD, not a
 * yes/no: the amount, the date and the exact words shown, kept on the file (`goals.incomeOnlyConfirmation`)
 * and printed in the advice document. It belongs to that amount: if the amount changes, the client is asked
 * again. "I never want to be sued" (owner).
 *
 * The words are the client's own statement, so the stored words are always in the client's voice; an adviser
 * viewing the file reads the same statement about the client.
 */
import React from 'react';
import type { DraftFactFind } from '../../constants/initialFactFind.ts';
import { householdVoice } from '../../utils/householdWords.ts';
import { formatCurrency } from '../../utils/index.ts';

/** The goals key the record is stored under. */
export const INCOME_ONLY_RECORD = 'incomeOnlyConfirmation' as const;

export interface IncomeOnlyConfirmation { amount?: number; date?: string; words?: string }

/** The client's statement, word for word, as stored and printed. */
export function incomeOnlyWords(amount: number): string {
    return `I understand that nothing from the ${formatCurrency(Math.round(amount))} will come back to me or my family, even if I pass away soon after buying it.`;
}

/**
 * A guaranteed income is "income only" when there is money in it, it is a lifetime annuity (not a set term,
 * not the annuity-and-life-cover type that pays the full amount back), and nothing is returned.
 */
export function isIncomeOnlyAnnuity(a: { purchaseAmount?: unknown; annuityType?: unknown; residualCapital?: unknown } | null | undefined): boolean {
    if (!a) return false;
    const amount = parseFloat(String(a.purchaseAmount ?? 0)) || 0;
    if (!(amount > 0)) return false;
    if (a.annuityType === 'fixedTerm' || a.annuityType === 'guaranteed') return false;
    return Math.round((Number(a.residualCapital) || 0) * 100) <= 0;
}

/** The stored record, if any. */
export function incomeOnlyRecordOf(factFind: DraftFactFind | null | undefined): IncomeOnlyConfirmation | null {
    const r = (factFind?.goals as { incomeOnlyConfirmation?: IncomeOnlyConfirmation } | undefined)?.incomeOnlyConfirmation;
    return r && Number(r.amount) > 0 ? r : null;
}

/** Whether the client has confirmed income only for THIS amount (to the dollar). */
export function incomeOnlyConfirmedFor(factFind: DraftFactFind | null | undefined, amount: number): boolean {
    const r = incomeOnlyRecordOf(factFind);
    return !!r && Math.abs(Number(r.amount) - Math.round(Number(amount) || 0)) < 1;
}

/** True when the plan cannot be chosen or saved yet: income only, and not confirmed for this amount. */
export function incomeOnlyHeld(factFind: DraftFactFind | null | undefined, incomeOnly: boolean, amount: number): boolean {
    return incomeOnly && !incomeOnlyConfirmedFor(factFind, amount);
}

const today = () => {
    const d = new Date();
    return `${d.getFullYear()}-${String(d.getMonth() + 1).padStart(2, '0')}-${String(d.getDate()).padStart(2, '0')}`;
};

/** The record to store for a confirmation of `amount` (dated today). */
export function incomeOnlyRecordFor(amount: number): IncomeOnlyConfirmation {
    return { amount: Math.round(amount), date: today(), words: incomeOnlyWords(amount) };
}

/** The writer for the record, from a module's setFactFind: confirm for an amount, or withdraw. */
export function incomeOnlySetter(setFactFind: React.Dispatch<React.SetStateAction<DraftFactFind>> | null | undefined): ((confirmed: boolean, amount: number) => void) | undefined {
    if (!setFactFind) return undefined;
    return (confirmed: boolean, amount: number) => setFactFind((prev) => {
        const goals = { ...((prev?.goals || {}) as Record<string, unknown>) };
        if (confirmed) goals[INCOME_ONLY_RECORD] = incomeOnlyRecordFor(amount);
        else delete goals[INCOME_ONLY_RECORD];
        return { ...prev, goals } as DraftFactFind;
    });
}

/** The statement as the reader sees it: the client's own words, or the same statement about the client. */
function statementFor(factFind: DraftFactFind | null | undefined, amount: number): string {
    const v = householdVoice(factFind);
    if (v.you) return incomeOnlyWords(amount);
    return `${v.Names} understand${v.s} that nothing from the ${formatCurrency(Math.round(amount))} will come back to ${v.them} or ${v.their} family, even if ${v.they} pass away soon after buying it.`;
}

interface TickProps {
    factFind: DraftFactFind | null | undefined;
    /** The amount going into the income-only guaranteed income. */
    amount: number;
    onChange: (confirmed: boolean, amount: number) => void;
    /** The confirmation as the module holds it; read from `factFind` when not given. */
    checked?: boolean;
    testId?: string;
}

/** Under the type cards when income only is picked: the consequence in plain words, and the confirmation. */
export const IncomeOnlyTick: React.FC<TickProps> = ({ factFind, amount, onChange, checked: given, testId = 'income-only-tick-box' }) => {
    const checked = given ?? incomeOnlyConfirmedFor(factFind, amount);
    return (
        <div className="rounded-lg border border-slate-200 border-l-4 border-l-amber-400 bg-white px-3 py-2 text-[12px] leading-relaxed text-slate-700" data-testid={testId}>
            <p>
                <strong className="text-slate-900">Income only.</strong> The highest income, and in return <strong>nothing comes back</strong>:
                if death comes, even soon after buying it, the family gets nothing from the {formatCurrency(amount)}, and it can&apos;t be cashed in if plans change.
            </p>
            <label className="mt-2 flex items-start gap-2">
                <input type="checkbox" checked={checked} onChange={(e) => onChange(e.target.checked, amount)} className="mt-0.5" data-testid="income-only-tick" />
                <span><strong>{statementFor(factFind, amount)}</strong> <span className="text-slate-500">Recorded with the amount and today&apos;s date, and printed in the advice document. Needed before choosing a plan with it.</span></span>
            </label>
        </div>
    );
};

interface BeforeChooseProps extends TickProps {
    /** "choosing" or "saving" — what the button beside it does. */
    action?: 'choosing' | 'saving';
}

/** Beside the choose or save button: the confirmation, and while it is missing, why the button is held. */
export const IncomeOnlyBeforeChoose: React.FC<BeforeChooseProps> = ({ factFind, amount, onChange, action = 'choosing', testId = 'income-only-before-choose' }) => {
    const v = householdVoice(factFind);
    const checked = incomeOnlyConfirmedFor(factFind, amount);
    const record = incomeOnlyRecordOf(factFind);
    return (
        <div className="space-y-1.5 rounded-lg border border-slate-200 border-l-4 border-l-amber-400 bg-white px-3 py-2 text-[13px] text-slate-700" data-testid={testId}>
            <p><strong className="text-slate-900">Before {action === 'saving' ? 'saving' : v.you ? 'you choose' : `${v.names} choose${v.s}`}:</strong></p>
            <label className="flex items-start gap-2">
                <input type="checkbox" checked={checked} onChange={(e) => onChange(e.target.checked, amount)} className="mt-0.5" data-testid="income-only-tick-repeat" />
                <span>{statementFor(factFind, amount)}</span>
            </label>
            {checked && record?.date && <p className="text-[12.5px] text-slate-500" data-testid="income-only-recorded">Recorded on {record.date}, and printed in the advice document.</p>}
            {!checked && (
                <p className="text-[12.5px] text-slate-500" data-testid="income-only-held">
                    The plan can&apos;t be {action === 'saving' ? 'saved' : 'chosen'} until this is ticked: its guaranteed income is the type where nothing comes back.
                    {record && Number(record.amount) > 0 ? ` (The earlier confirmation was for ${formatCurrency(Number(record.amount))}; the amount has changed.)` : ''}
                </p>
            )}
        </div>
    );
};
